import React, { useState, useEffect } from "react";
import moment from "moment-timezone";
import { RedisAdminData } from "../types";

interface PortfolioTabProps {
  data: RedisAdminData;
}

type PeriodType = "day" | "week" | "biweek" | "month" | "3months" | "6months";

interface PeriodInfo {
  label: string;
  startDate: string;
  endDate: string;
}

function safeParse(v: any): { CREATED_AT?: string; unlock?: boolean } | null {
  try {
    return typeof v === "string" ? JSON.parse(v) : v;
  } catch {
    return null;
  }
}

export const PortfolioTab: React.FC<PortfolioTabProps> = ({ data }) => {
  const teaserEntries = Object.entries(data.portfolioTeaserLog || {});
  const teaserTotal = teaserEntries.length;
  const teaserUnlockCount = teaserEntries.reduce((acc, [, v]) => {
    const obj = typeof v === "string" ? safeParse(v) : v;
    return acc + (obj?.unlock ? 1 : 0);
  }, 0);

  // 컷오프 기준: 2025-09-19 00:00 (KST)
  const CUTOFF_KST = moment.tz(
    "2025-09-19 00:00",
    "YYYY-MM-DD HH:mm",
    "Asia/Seoul"
  );
  // 데이터 포맷 전환 기준(기존 로직과 동일하게 보정): 2025-09-23 02:32:17
  const ADJUST_CUTOFF = moment(
    "2025-09-23 02:32:17",
    "YYYY-MM-DD HH:mm:ss",
    true
  );

  const teaserEntriesFiltered = teaserEntries.filter(([, v]) => {
    const obj = typeof v === "string" ? safeParse(v) : v;
    const rawCreatedAt = obj?.CREATED_AT ?? "";
    const parsed = moment(rawCreatedAt, "YYYY-MM-DD HH:mm:ss", true);
    const adjusted = parsed.isValid()
      ? parsed.isBefore(ADJUST_CUTOFF)
        ? parsed.clone().add(9, "hours")
        : parsed
      : null;
    const ts = adjusted ? adjusted.valueOf() : Number.NEGATIVE_INFINITY;
    return ts >= CUTOFF_KST.valueOf();
  });

  const teaserTotalFiltered = teaserEntriesFiltered.length;
  const teaserUnlockCountFiltered = teaserEntriesFiltered.reduce(
    (acc, [, v]) => {
      const obj = typeof v === "string" ? safeParse(v) : v;
      return acc + (obj?.unlock ? 1 : 0);
    },
    0
  );

  if (!data.lastView) {
    return (
      <div className="glass-card">
        <div className="text-white/80">포트폴리오 데이터가 없습니다.</div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <section className="glass-card">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-3xl font-bold tracking-tight text-white/90 glow-text">
            포트폴리오
          </h2>
          <span className="text-sm text-cyan-300/80 font-mono bg-white/10 px-3 py-1 rounded-full backdrop-blur-sm">
            키: {data.lastView.key}
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="glass-sub-card">
            <h3 className="text-lg font-semibold text-white/90 mb-4 glow-text">
              사용자 통계
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-white/70 text-base">총 사용자</span>
                <span className="text-3xl font-bold text-white/95">
                  {data.lastView.totalUsers.toLocaleString()}
                </span>
              </div>
              <div className="h-px bg-white/10"></div>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-base text-white/60 mb-1">DAU</div>
                  <div className="text-2xl font-bold text-cyan-300">
                    {data.lastView.dau}
                  </div>
                </div>
                <div>
                  <div className="text-base text-white/60 mb-1">WAU</div>
                  <div className="text-2xl font-bold text-violet-300">
                    {data.lastView.wau}
                  </div>
                </div>
                <div>
                  <div className="text-base text-white/60 mb-1">MAU</div>
                  <div className="text-2xl font-bold text-pink-300">
                    {data.lastView.mau}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="glass-sub-card">
            <h3 className="text-lg font-semibold text-white/90 mb-4 glow-text">
              실시간 활동
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-white/70 text-base">오늘</span>
                <span className="text-3xl font-bold text-emerald-300">
                  {data.lastView.activeToday}
                </span>
              </div>
              <div className="h-px bg-white/10"></div>
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <div className="text-base text-white/60 mb-1">최근 3시간</div>
                  <div className="text-2xl font-bold text-cyan-300">
                    {data.lastView.activeLast3h}
                  </div>
                </div>
                <div>
                  <div className="text-base text-white/60 mb-1">
                    최근 24시간
                  </div>
                  <div className="text-2xl font-bold text-violet-300">
                    {data.lastView.activeLast24h}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="glass-sub-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white/90 glow-text">
                티저 성과
              </h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-white/70 text-base">전환율</span>
                <span className="text-3xl font-bold text-pink-300">
                  {(
                    (teaserUnlockCountFiltered /
                      Math.max(1, teaserTotalFiltered)) *
                    100
                  ).toFixed(1)}
                  %
                </span>
              </div>
              <div className="h-px bg-white/10"></div>
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <div className="text-base text-white/60 mb-1">전체</div>
                  <div className="text-2xl font-bold text-cyan-300">
                    {teaserTotalFiltered}
                  </div>
                </div>
                <div>
                  <div className="text-base text-white/60 mb-1">언락</div>
                  <div className="text-2xl font-bold text-violet-300">
                    {teaserUnlockCountFiltered}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <Card title="활성/비활성 분포">
              <BucketBar
                buckets={data.lastView.inactiveBuckets}
                total={data.lastView.totalUsers}
              />
            </Card>
          </div>
          <div className="lg:col-span-2">
            <Card
              title={`시간대 히트맵 (최근 ${data.lastView.heatmap.windowDays}일)`}
              extra={<HeatmapLegend />}
            >
              <Heatmap matrix={data.lastView.heatmap.matrix} />
            </Card>
          </div>
        </div>

        <div className="mt-8">
          <Card title="티저 상세">
            <TeaserTabs data={data.portfolioTeaserLog || {}} />
          </Card>
        </div>
      </section>

      {/* 인기 포트폴리오 종목 TOP 10 */}
      <section className="glass-card">
        <PortfolioStocksRanking />
      </section>
    </div>
  );
};

function Card({
  title,
  children,
  extra,
}: {
  title: string;
  children: React.ReactNode;
  extra?: React.ReactNode;
}) {
  return (
    <div className="glass-sub-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold tracking-tight text-white/90 text-lg glow-text">
          {title}
        </h3>
        {extra && <div className="flex-shrink-0">{extra}</div>}
      </div>
      {children}
    </div>
  );
}

function TeaserTable({
  data,
}: {
  data: Record<string, { CREATED_AT?: string; unlock?: boolean } | string>;
}) {
  const [onlyUnlocked, setOnlyUnlocked] = React.useState(false);
  const [active, setActive] = React.useState<"filtered" | "past">("filtered");

  if (!data || Object.keys(data).length === 0)
    return (
      <div className="text-center py-8">
        <div className="text-white/60 text-lg glow-text">데이터 없음</div>
      </div>
    );

  const cutoff = moment("2025-09-23 02:32:17", "YYYY-MM-DD HH:mm:ss", true);
  const cutoffLogs = moment.tz(
    "2025-09-19 00:00",
    "YYYY-MM-DD HH:mm",
    "Asia/Seoul"
  );

  const rowsAll = Object.entries(data).map(([id, raw]) => {
    const obj = typeof raw === "string" ? safeParse(raw) : raw;
    const rawCreatedAt = obj?.CREATED_AT ?? "";
    const parsed = moment(rawCreatedAt, "YYYY-MM-DD HH:mm:ss", true);
    const adjusted = parsed.isValid()
      ? parsed.isBefore(cutoff)
        ? parsed.clone().add(9, "hours")
        : parsed
      : null;
    const createdAtDisplay = adjusted
      ? adjusted.format("YYYY-MM-DD HH:mm:ss")
      : rawCreatedAt;
    const createdAtTs = adjusted
      ? adjusted.valueOf()
      : Number.NEGATIVE_INFINITY;
    return {
      id,
      CREATED_AT: createdAtDisplay,
      CREATED_AT_TS: createdAtTs,
      unlock: obj?.unlock ?? false,
      email: (obj as any)?.email ?? "",
    };
  });

  // 정렬 안정화: 동률이면 id 내림차순
  const toIdNum = (id: string) => {
    const n = Number(id);
    return Number.isFinite(n) ? n : 0;
  };

  const rowsSorted = rowsAll.slice().sort((a, b) => {
    const diff = b.CREATED_AT_TS - a.CREATED_AT_TS;
    if (diff !== 0) return diff;
    return toIdNum(String(b.id)) - toIdNum(String(a.id));
  });

  const rowsFiltered = rowsSorted.filter(
    (r) => r.CREATED_AT_TS >= cutoffLogs.valueOf()
  );
  const rowsPast = rowsSorted.filter(
    (r) => r.CREATED_AT_TS < cutoffLogs.valueOf()
  );

  const filteredRows = (active: "filtered" | "past") => {
    const base = active === "filtered" ? rowsFiltered : rowsPast;
    return base.filter((r) =>
      onlyUnlocked ? r.unlock === true || String(r.unlock) === "true" : true
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex gap-4 items-center">
          <button
            className={`px-3 py-2 text-sm rounded-lg font-semibold ${
              active === "filtered"
                ? "bg-white/20 text-white border border-white/30"
                : "text-white/70 hover:text-white/90 hover:bg-white/10"
            }`}
            onClick={() => setActive("filtered")}
          >
            운영 데이터
          </button>
          <button
            className={`px-3 py-2 text-sm rounded-lg font-semibold ${
              active === "past"
                ? "bg-white/20 text-white border border-white/30"
                : "text-white/70 hover:text-white/90 hover:bg-white/10"
            }`}
            onClick={() => setActive("past")}
          >
            25.9.19 이전
          </button>
        </div>

        <div className="flex gap-2">
          <button
            className="glass-button px-3 py-2 text-sm"
            onClick={() => setOnlyUnlocked(!onlyUnlocked)}
          >
            {onlyUnlocked ? "전체 보기" : "언락만 보기"}
          </button>
        </div>
      </div>
      <div className="max-h-[80vh] glass-table-container scrollable-table">
        <table className="min-w-full text-sm">
          <thead className="sticky top-0 bg-white/10 backdrop-blur-md z-10">
            <tr>
              <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20">
                No
              </th>
              <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20">
                ID
              </th>

              <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20">
                CREATED_AT (KST)
              </th>
              <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20">
                언락
              </th>
              <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20">
                이메일
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredRows(active).map((r, index) => (
              <tr
                key={`teaser:${r.id}`}
                className={index % 2 === 0 ? "bg-white/2" : ""}
              >
                <td className="px-4 py-3 text-white/80 border-b border-white/10 font-mono text-base ">
                  {filteredRows(active).length - index}
                </td>
                <td className="px-4 py-3 text-white/80 border-b border-white/10 font-mono text-base ">
                  {r.id}
                </td>

                <td className="px-4 py-3 text-white/80 border-b border-white/10 font-mono text-base ">
                  {r.CREATED_AT}
                </td>
                <td className="px-4 py-3 border-b border-white/10">
                  <span
                    className={`inline-flex w-6 h-6 items-center justify-center rounded-full text-base font-bold ${
                      String(r.unlock) === "true"
                        ? "bg-emerald-500/20 text-emerald-300 border border-emerald-400/30"
                        : "bg-red-500/20 text-red-300 border border-red-400/30"
                    }`}
                  >
                    {String(r.unlock) === "true" ? "✓" : "✗"}
                  </span>
                </td>
                <td className="px-4 py-3 text-white/70 font-mono text-base border-b border-white/10">
                  {r.email ? r.email : "-"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TeaserTabs({
  data,
}: {
  data: Record<string, { CREATED_AT?: string; unlock?: boolean } | string>;
}) {
  return <TeaserTable data={data} />;
}

function BucketBar({
  buckets,
  total,
}: {
  buckets: Record<string, number>;
  total: number;
}) {
  const order: Array<keyof typeof buckets> = [
    "0_1d",
    "1_7d",
    "7_14d",
    "14_30d",
    "30p",
  ];
  const colors = ["#06d6a0", "#118ab2", "#073b4c", "#ef476f", "#ffd166"];
  const items = order.map((k, i) => ({
    key: k,
    count: buckets[k] || 0,
    color: colors[i],
  }));
  const sum = items.reduce((a, b) => a + b.count, 0) || 1;

  return (
    <div>
      <div className="flex h-8 w-full rounded-xl overflow-hidden backdrop-blur-sm bg-white/10 border border-white/20 shadow-lg">
        {items.map((it) => (
          <div
            key={String(it.key)}
            className="h-full"
            style={{
              width: `${(it.count / sum) * 100}%`,
              background: `linear-gradient(135deg, ${it.color}dd, ${it.color}aa)`,
              boxShadow: `inset 0 1px 0 rgba(255,255,255,0.2)`,
            }}
            title={`${it.key}: ${it.count}`}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-4 mt-4 text-sm text-white/80">
        {items.map((it) => (
          <div key={String(it.key)} className="inline-flex items-center gap-2">
            <span
              className="inline-block w-4 h-4 rounded-full border border-white/30 shadow-sm"
              style={{ backgroundColor: it.color }}
            />
            <span className="font-medium">{labelOfBucket(String(it.key))}</span>
            <span className="text-cyan-300 font-mono text-base bg-white/10 px-2 py-0.5 rounded-full">
              {it.count}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function labelOfBucket(k: string) {
  switch (k) {
    case "0_1d":
      return "최근 1일 이내";
    case "1_7d":
      return "1–7일";
    case "7_14d":
      return "7–14일";
    case "14_30d":
      return "14–30일";
    case "30p":
      return "30일+";
    default:
      return k;
  }
}

function HeatmapLegend() {
  const legendItems = [
    { range: "0", color: "rgba(15, 23, 42, 0.4)", label: "없음" },
    { range: "1-3", color: "rgba(30, 58, 138, 0.5)", label: "매우 낮음" },
    { range: "4-10", color: "rgba(67, 56, 202, 0.7)", label: "낮음" },
    { range: "11-20", color: "rgba(37, 99, 235, 0.8)", label: "보통" },
    { range: "21+", color: "rgba(59, 130, 246, 0.9)", label: "높음" },
  ];

  return (
    <div className="flex items-center gap-3 text-xs">
      {legendItems.map((item, index) => (
        <div key={index} className="flex items-center gap-1">
          <div
            className="w-3 h-3 rounded border border-white/20"
            style={{ backgroundColor: item.color }}
          />
          <span className="text-white/70 whitespace-nowrap">{item.range}</span>
        </div>
      ))}
    </div>
  );
}

function Heatmap({ matrix }: { matrix: number[][] }) {
  const weekdays = ["일", "월", "화", "수", "목", "금", "토"];
  const hours = Array.from(
    { length: 24 },
    (_, i) => `${String(i).padStart(2, "0")}시`
  );
  const flat = matrix.flat();
  const max = Math.max(1, ...flat);
  const color = (v: number) => {
    if (v === 0) {
      return { backgroundColor: "rgba(15, 23, 42, 0.4)", boxShadow: "none" };
    }

    let backgroundColor, glowColor;

    if (v <= 3) {
      const intensity = Math.min(v / 3, 1);
      backgroundColor = `rgba(30, 58, 138, ${0.3 + intensity * 0.3})`;
      glowColor = `rgba(59, 130, 246, ${intensity * 0.2})`;
    } else if (v <= 10) {
      const intensity = Math.min((v - 3) / 7, 1);
      backgroundColor = `rgba(67, 56, 202, ${0.5 + intensity * 0.3})`;
      glowColor = `rgba(99, 102, 241, ${0.3 + intensity * 0.3})`;
    } else if (v <= 20) {
      const intensity = Math.min((v - 10) / 10, 1);
      backgroundColor = `rgba(37, 99, 235, ${0.7 + intensity * 0.2})`;
      glowColor = `rgba(96, 165, 250, ${0.5 + intensity * 0.3})`;
    } else {
      const intensity = Math.min((v - 20) / 15, 1);
      backgroundColor = `rgba(59, 130, 246, ${0.8 + intensity * 0.2})`;
      glowColor = `rgba(147, 197, 253, ${0.7 + intensity * 0.3})`;
    }

    return {
      backgroundColor,
      boxShadow:
        v > 5 ? `0 0 ${Math.round((v / 35) * 8 + 2)}px ${glowColor}` : "none",
    };
  };

  return (
    <div className="overflow-auto rounded-2xl bg-white/5 backdrop-blur-sm border border-white/20 shadow-2xl p-2">
      <table className="text-base table-fixed">
        <colgroup>
          <col className="w-16" />
          {Array.from({ length: 24 }).map((_, i) => (
            <col key={i} className="w-8" />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th className="p-2 text-left text-xs text-white/80 font-semibold">
              요일/시간
            </th>
            {hours.map((h) => (
              <th
                key={h}
                className="px-1 py-2 text-white/70 font-normal whitespace-nowrap font-mono text-center text-base"
              >
                {h.replace("시", "")}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {matrix.map((row, wd) => (
            <tr key={wd}>
              <td className="pr-2 py-1 text-white/80 whitespace-nowrap font-medium text-center">
                {weekdays[wd]}
              </td>
              {row.map((v, hr) => (
                <td key={hr} className="p-0.5">
                  <div
                    className="w-7 h-7 rounded-lg mx-auto border border-white/10 cursor-pointer hover:scale-110 transition-all duration-200 hover:border-white/30"
                    title={`${weekdays[wd]}요일 ${hr}시: ${v}건`}
                    style={color(v)}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// 포트폴리오 종목 랭킹 컴포넌트
function PortfolioStocksRanking() {
  const [periodType, setPeriodType] = useState<PeriodType>("month");
  const [currentPeriodOffset, setCurrentPeriodOffset] = useState(0);
  const [periodInfo, setPeriodInfo] = useState<PeriodInfo | null>(null);
  const [topStocksData, setTopStocksData] = useState<
    Array<{
      ticker: string;
      stockName: string;
      userCount: number;
    }>
  >([]);
  const [isLoading, setIsLoading] = useState(false);

  // 기간 계산 함수 (InterestTab과 동일)
  const calculatePeriod = (type: PeriodType, offset: number): PeriodInfo => {
    const today = new Date();
    let startDate: Date;
    let endDate: Date;
    let label: string;

    switch (type) {
      case "day": {
        const targetDate = new Date(today);
        targetDate.setDate(today.getDate() + offset);
        startDate = new Date(targetDate);
        endDate = new Date(targetDate);
        label = formatDate(startDate);
        break;
      }
      case "week": {
        const currentDay = today.getDay();
        const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay;
        const thisMonday = new Date(today);
        thisMonday.setDate(today.getDate() + mondayOffset);

        const targetMonday = new Date(thisMonday);
        targetMonday.setDate(thisMonday.getDate() + offset * 7);

        startDate = new Date(targetMonday);
        endDate = new Date(targetMonday);
        endDate.setDate(targetMonday.getDate() + 6);

        if (offset === 0 && endDate > today) {
          endDate = new Date(today);
        }

        label = `${formatDate(startDate)} ~ ${formatDate(endDate)}`;
        break;
      }
      case "biweek": {
        const currentDay = today.getDate();
        const isCurrentFirstHalf = currentDay <= 14;

        let periodsBack = -offset;

        let tempMonth = today.getMonth();
        let tempYear = today.getFullYear();
        let tempIsFirstHalf = isCurrentFirstHalf;

        while (periodsBack > 0) {
          if (tempIsFirstHalf) {
            tempMonth--;
            if (tempMonth < 0) {
              tempMonth = 11;
              tempYear--;
            }
            tempIsFirstHalf = false;
          } else {
            tempIsFirstHalf = true;
          }
          periodsBack--;
        }

        if (tempIsFirstHalf) {
          startDate = new Date(tempYear, tempMonth, 1);
          endDate = new Date(tempYear, tempMonth, 14);
        } else {
          startDate = new Date(tempYear, tempMonth, 15);
          endDate = new Date(tempYear, tempMonth + 1, 0);
        }

        if (offset === 0 && endDate > today) {
          endDate = new Date(today);
        }

        label = `${formatDate(startDate)} ~ ${formatDate(endDate)}`;
        break;
      }
      case "month": {
        const targetMonth = new Date(
          today.getFullYear(),
          today.getMonth() + offset,
          1
        );
        startDate = new Date(
          targetMonth.getFullYear(),
          targetMonth.getMonth(),
          1
        );
        endDate = new Date(
          targetMonth.getFullYear(),
          targetMonth.getMonth() + 1,
          0
        );

        if (offset === 0) {
          endDate = new Date(today);
        }

        label = `${targetMonth.getFullYear()}년 ${
          targetMonth.getMonth() + 1
        }월`;
        break;
      }
      case "3months": {
        const targetMonth = new Date(
          today.getFullYear(),
          today.getMonth() + offset * 3,
          1
        );
        startDate = new Date(
          targetMonth.getFullYear(),
          targetMonth.getMonth(),
          1
        );
        endDate = new Date(
          targetMonth.getFullYear(),
          targetMonth.getMonth() + 3,
          0
        );

        if (offset === 0) {
          endDate = new Date(today);
        }

        const endMonth = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
        label = `${targetMonth.getFullYear()}년 ${
          targetMonth.getMonth() + 1
        }월 ~ ${endMonth.getFullYear()}년 ${endMonth.getMonth() + 1}월`;
        break;
      }
      case "6months": {
        const targetMonth = new Date(
          today.getFullYear(),
          today.getMonth() + offset * 6,
          1
        );
        startDate = new Date(
          targetMonth.getFullYear(),
          targetMonth.getMonth(),
          1
        );
        endDate = new Date(
          targetMonth.getFullYear(),
          targetMonth.getMonth() + 6,
          0
        );

        if (offset === 0) {
          endDate = new Date(today);
        }

        const endMonth = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
        label = `${targetMonth.getFullYear()}년 ${
          targetMonth.getMonth() + 1
        }월 ~ ${endMonth.getFullYear()}년 ${endMonth.getMonth() + 1}월`;
        break;
      }
    }

    return {
      label,
      startDate: formatDateForAPI(startDate),
      endDate: formatDateForAPI(endDate),
    };
  };

  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const formatDateForAPI = (date: Date): string => {
    return formatDate(date);
  };

  // 데이터 페칭 함수
  const fetchTopStocks = async (start: string, end: string) => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `/api/admin/mysql?type=topPortfolioStocksByPeriod&startDate=${start}&endDate=${end}`
      );
      const newData = await response.json();
      setTopStocksData(newData);
    } catch (error) {
      console.error("Failed to fetch top portfolio stocks:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // 기간 변경 시 데이터 업데이트
  useEffect(() => {
    const period = calculatePeriod(periodType, currentPeriodOffset);
    setPeriodInfo(period);
    fetchTopStocks(period.startDate, period.endDate);
  }, [periodType, currentPeriodOffset]);

  // 기간 타입 변경 핸들러
  const handlePeriodTypeChange = (newType: PeriodType) => {
    setPeriodType(newType);
    setCurrentPeriodOffset(0);
  };

  // 이전/다음 기간 이동 핸들러
  const handlePrevPeriod = () => {
    setCurrentPeriodOffset(currentPeriodOffset - 1);
  };

  const handleNextPeriod = () => {
    if (currentPeriodOffset < 0) {
      setCurrentPeriodOffset(currentPeriodOffset + 1);
    }
  };

  const canGoNext = currentPeriodOffset < 0;

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-white/90 glow-text mb-4">
          인기 포트폴리오 종목 TOP 50
        </h2>

        {/* 기간 선택 및 네비게이션 */}
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          {/* 기간 타입 선택 */}
          <div className="flex gap-2 flex-wrap">
            <PeriodButton
              label="1일"
              active={periodType === "day"}
              onClick={() => handlePeriodTypeChange("day")}
            />
            <PeriodButton
              label="1주"
              active={periodType === "week"}
              onClick={() => handlePeriodTypeChange("week")}
            />
            <PeriodButton
              label="2주"
              active={periodType === "biweek"}
              onClick={() => handlePeriodTypeChange("biweek")}
            />
            <PeriodButton
              label="1개월"
              active={periodType === "month"}
              onClick={() => handlePeriodTypeChange("month")}
            />
            <PeriodButton
              label="3개월"
              active={periodType === "3months"}
              onClick={() => handlePeriodTypeChange("3months")}
            />
            <PeriodButton
              label="6개월"
              active={periodType === "6months"}
              onClick={() => handlePeriodTypeChange("6months")}
            />
          </div>

          {/* 기간 네비게이션 */}
          <div className="flex items-center gap-3 md:ml-auto">
            <button
              onClick={handlePrevPeriod}
              className="p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 transition-all"
              title="이전 기간"
            >
              <svg
                className="w-5 h-5 text-white/70"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            <div className="px-4 py-2 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border border-blue-400/30 rounded-lg min-w-[200px] text-center">
              <span className="text-white/90 font-medium text-sm">
                {periodInfo?.label || ""}
              </span>
            </div>

            <button
              onClick={handleNextPeriod}
              disabled={!canGoNext}
              className={`p-2 rounded-lg border transition-all ${
                canGoNext
                  ? "bg-white/5 hover:bg-white/10 border-white/10 hover:border-white/20"
                  : "bg-white/2 border-white/5 cursor-not-allowed opacity-40"
              }`}
              title="다음 기간"
            >
              <svg
                className="w-5 h-5 text-white/70"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <PortfolioStocksTable data={topStocksData} isLoading={isLoading} />
    </div>
  );
}

function PeriodButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 rounded-lg border transition-all text-sm font-medium ${
        active
          ? "bg-gradient-to-r from-blue-500/30 to-cyan-500/30 border-blue-400/50 text-white shadow-lg shadow-blue-500/20"
          : "bg-white/5 border-white/10 text-white/70 hover:bg-white/10 hover:border-white/20"
      }`}
    >
      {label}
    </button>
  );
}

function PortfolioStocksTable({
  data,
  isLoading,
}: {
  data: Array<{
    ticker: string;
    stockName: string;
    userCount: number;
  }>;
  isLoading?: boolean;
}) {
  if (isLoading) {
    return (
      <div className="text-center py-12 h-[80vh]">
        {/* <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-400"></div>
        <div className="text-white/60 text-lg glow-text mt-4"></div> */}
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-8 h-[80vh]">
        <div className="text-white/60 text-lg glow-text">데이터 없음</div>
      </div>
    );
  }

  return (
    <div className="glass-table-container scrollable-table h-[80vh]">
      <table className="min-w-full text-sm">
        <thead className="sticky top-0 bg-white/10 backdrop-blur-md z-10">
          <tr>
            <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20 w-16">
              순위
            </th>
            <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20">
              티커
            </th>
            <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20">
              종목명
            </th>
            <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20">
              담은 사용자 수
            </th>
          </tr>
        </thead>
        <tbody>
          {data.map((stock, index) => (
            <tr
              key={stock.ticker}
              className={index % 2 === 0 ? "bg-white/2" : ""}
            >
              <td className="px-4 py-3 border-b border-white/10">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                    index === 0
                      ? "bg-gradient-to-r from-yellow-400 to-yellow-600 text-yellow-900"
                      : index === 1
                      ? "bg-gradient-to-r from-gray-300 to-gray-500 text-gray-900"
                      : index === 2
                      ? "bg-gradient-to-r from-amber-600 to-amber-800 text-amber-100"
                      : "bg-white/10 text-white/70"
                  }`}
                >
                  {index + 1}
                </div>
              </td>
              <td className="px-4 py-3 text-cyan-300 font-mono text-base border-b border-white/10">
                {stock.ticker}
              </td>
              <td className="px-4 py-3 text-white/80 border-b border-white/10 text-base font-medium">
                {stock.stockName || "-"}
              </td>
              <td className="px-4 py-3 text-violet-300 font-bold border-b border-white/10 text-base">
                {stock.userCount.toLocaleString()}명
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
